'use client';

import { asText, isFilled } from '@prismicio/client';
import type { LinkField, RichTextField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import { useEffect, useMemo } from 'react';
import { CloseIcon } from '@/assets';
import { useDismissibleBar, useIsClient } from '@/hooks';

export type AnnouncementBarItem = {
  message?: RichTextField;
  link?: LinkField;
  link_label?: string | null;
};

interface AnnouncementBarProps {
  /**
   * The `announcement_bar` group from the `global_navigation` Single Type.
   * Only the first item is rendered (the bar is global and singular); the
   * group shape is reused so editors get the same authoring UX as before.
   */
  items?: ReadonlyArray<AnnouncementBarItem>;
}

// Must match the offset baked into the sticky header in `layouts/General.tsx`
// and the dropdown panel in `GlobalNavBarRedesign.tsx`, both of which read
// the `--announcement-bar-height` CSS variable this component owns.
const BAR_HEIGHT = '44px';

/**
 * Site-wide announcement bar, configured once in Prismic
 * (`global_navigation.announcement_bar`) and rendered from `GeneralLayout` so
 * it appears on every page that layout wraps (case studies + the Velx-style
 * pages). Replaces the per-hero announcement bar that used to live inside the
 * `HeroVelxEngine` slice.
 *
 * It is dismissible (persisted in `localStorage`) and reserves its own space by
 * setting `--announcement-bar-height` + `body` padding, exactly as the old
 * hero-level bar did, so the sticky navbar keeps sitting just below it.
 */
export const AnnouncementBar = ({ items }: AnnouncementBarProps) => {
  const item = items?.[0] ?? null;

  const hasContent =
    !!item &&
    (isFilled.richText(item.message) ||
      (isFilled.link(item.link) && !!item.link_label));

  // Key the dismissal by the bar's content so publishing a new announcement
  // re-shows it to visitors who dismissed the previous one.
  const storageKey = useMemo(() => {
    const base = item ? `${asText(item.message)}|${item.link_label ?? ''}` : '';
    return `global-announcement:${encodeURIComponent(base)}:dismissed`;
  }, [item]);

  const isClient = useIsClient();
  const { isDismissed, dismiss } = useDismissibleBar(storageKey);

  const isVisible = isClient && hasContent && !isDismissed;

  useEffect(() => {
    if (!isClient) {
      return;
    }
    const root = document.documentElement;
    const previousPaddingTop = document.body.style.paddingTop;
    const previousBarHeight = root.style.getPropertyValue(
      '--announcement-bar-height',
    );
    if (isVisible) {
      document.body.style.paddingTop = BAR_HEIGHT;
      root.style.setProperty('--announcement-bar-height', BAR_HEIGHT);
    } else {
      document.body.style.paddingTop = '';
      root.style.setProperty('--announcement-bar-height', '0px');
    }
    return () => {
      document.body.style.paddingTop = previousPaddingTop;
      root.style.setProperty('--announcement-bar-height', previousBarHeight);
    };
  }, [isClient, isVisible]);

  if (!isVisible || !item) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-center bg-[#21007c] px-4 py-2.5">
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 md:gap-4">
        {isFilled.richText(item.message) && (
          <PrismicRichText
            field={item.message}
            components={{
              paragraph: ({ children }) => (
                <p className="m-0 text-center text-[14px] font-medium leading-[1.4] text-white">
                  {children}
                </p>
              ),
            }}
          />
        )}
        {isFilled.link(item.link) && item.link_label && (
          <PrismicNextLink
            field={item.link}
            className="shrink-0 whitespace-nowrap text-[16px] font-extrabold leading-[1.5] text-[#00f557]"
          >
            {item.link_label}
          </PrismicNextLink>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="absolute right-4 shrink-0 p-1 text-white transition-opacity hover:opacity-70 md:right-8"
      >
        <CloseIcon width={24} height={24} fill="white" />
      </button>
    </div>
  );
};
